/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskEvalReviewsRequest,
  ListRiskEvalReviewsSecurity,
} from "../models/operations/listriskevalreviews.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListEvalReviewsQuery,
  prefetchRiskListEvalReviews,
  queryKeyRiskListEvalReviews,
  RiskListEvalReviewsQueryData,
} from "./riskListEvalReviews.core.js";
export {
  buildRiskListEvalReviewsQuery,
  prefetchRiskListEvalReviews,
  queryKeyRiskListEvalReviews,
  type RiskListEvalReviewsQueryData,
};

export type RiskListEvalReviewsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskEvalReviews risk
 *
 * @remarks
 * List the active regression set for a prompt-based policy: every reviewer's current ground-truth verdicts.
 */
export function useRiskListEvalReviews(
  request: ListRiskEvalReviewsRequest,
  security?: ListRiskEvalReviewsSecurity | undefined,
  options?: QueryHookOptions<
    RiskListEvalReviewsQueryData,
    RiskListEvalReviewsQueryError
  >,
): UseQueryResult<RiskListEvalReviewsQueryData, RiskListEvalReviewsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListEvalReviewsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskEvalReviews risk
 *
 * @remarks
 * List the active regression set for a prompt-based policy: every reviewer's current ground-truth verdicts.
 */
export function useRiskListEvalReviewsSuspense(
  request: ListRiskEvalReviewsRequest,
  security?: ListRiskEvalReviewsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListEvalReviewsQueryData,
    RiskListEvalReviewsQueryError
  >,
): UseSuspenseQueryResult<
  RiskListEvalReviewsQueryData,
  RiskListEvalReviewsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListEvalReviewsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListEvalReviewsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      policyId: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListEvalReviewsQueryData,
): RiskListEvalReviewsQueryData | undefined {
  const key = queryKeyRiskListEvalReviews(...queryKeyBase);

  return client.setQueryData<RiskListEvalReviewsQueryData>(key, data);
}

export function invalidateRiskListEvalReviews(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      policyId: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "evals", "listReviews", ...queryKeyBase],
  });
}

export function invalidateAllRiskListEvalReviews(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "evals", "listReviews"],
  });
}
